// **The Beekeeper chapter's prompt bar** (`demo-presentation-shell` tasks 10.7-10.10, design
// **DD6**/**DD8**). Chapter 3's "Beekeeper" scope: the same `PromptBar` control chapter 1b uses,
// but the destination changes entirely — no `POST /demo/instruct` call at all. Instead this
// mints/reuses the player's own **practice sandbox** (exactly the surface `rounds-practice-sandbox`
// ships), seeds it with the demo colony's starting programs via the existing whole-document
// hand-edit route, and conducts the turn through the **shipped** `ChatOverlay` over the **existing**
// SSE turn transport (`app/rounds/chat-channel.ts` + `net/rounds-http.ts`'s `createChatClient`).
// **No second chat component, no second turn protocol** is introduced here — every network call
// this file makes is a call the practice sandbox already exposes.
//
// When a turn settles a changed document, `chat-channel.ts`'s own "closing the loop" behaviour
// re-fetches the colony and republishes it through the store seam below — `applyColonyMeta` is
// where that lands, compiling and hot-swapping (or creating) each type whose canonical text
// changed against the **local wasm world**, never the practice sandbox's own document a second
// time. No batch run, no trace, no navigation (DD8).

import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { DemoStageDriver, DemoStageTypeSpec } from './stage-driver';
import { c1BuffName } from './stage-json';
import { PromptBar } from './PromptBar';
import type { PromptBarStatus } from './PromptBar';
import { ChatOverlay } from '../../ui/components/chat/ChatOverlay';
import { useChatSeam } from '../../ui/colony/chat-seam';
import type { ChatSeam } from '../../ui/colony/chat-seam';
import type { ChatTransport } from '../rounds/chat-channel';
import { createChatChannel } from '../rounds/chat-channel';
import {
  createPracticeCredentialStore,
  cryptoPracticeRandom,
  type PracticeRandom,
} from '../rounds/practice-credential-store';
import type { StorageLike } from '../rounds/rounds-credential-store';
import { openPracticeSession, type OpenPracticeSessionConfig, type PracticeOpenOutcome } from '../../net/rounds-practice-http';
import { RoundsHttpClient, type HandEditDispatch, type RoundsHttpConfig } from '../../net/rounds-http';
import type { ColonyMeta } from '../../net/wire/messages';

/** The chat transport `RoundsHttpClient.createChatClient()` answers with, narrowed to what
 * `chat-channel.ts`'s `ChatTransport` actually reads off it (everything but `fetchColony`, which
 * this component supplies separately from `client` itself). A real `ChatSseClient` satisfies this
 * structurally; so does a test double with no SSE machinery behind it at all. */
export type DemoPracticeChatClient = Pick<
  ChatTransport,
  'sendMessage' | 'attachTurn' | 'stopTurn' | 'fetchThread' | 'clearThread'
>;

/** The subset of `RoundsHttpClient` this component drives — a seam so a test can prove the
 * **real** `createChatChannel`/`ChatOverlay` (imported directly below, never injected) are what
 * actually run, while only the network-boundary construction (mint a session, build the client)
 * is doubled — the same seam depth `fetchImpl` injection already uses everywhere else in this
 * client. */
export interface DemoPracticeClient {
  handEditDocument(source: string, baseVersion: number | null): Promise<HandEditDispatch>;
  createChatClient(): DemoPracticeChatClient;
  fetchColony(): Promise<ColonyMeta | null>;
}

export interface BeekeeperPromptBarProps {
  driver: DemoStageDriver;
  colony?: number;
  birthType: string;
  /** What the practice sandbox's document is seeded with — the demo colony's own starting
   * programs (`chapters.ts`'s `CONTINUOUS_STAGE`, in production wiring), so the agent's tools read
   * the same starting point the audience is watching. */
  seedTypes: readonly DemoStageTypeSpec[];
  /**
   * **Closes the chat overlay when the show moves past the conversation** (E2E round-3 polish).
   * The overlay is the presenter's feedback surface for the Beekeeper turn, and it stayed over the
   * fast-forward and the full-hive reveal — the best screen in the deck — until someone found its
   * ✕. Set once, at the fast-forward cue; a later message reopens it as usual.
   */
  collapseChat?: boolean;
  /**
   * **The colony's types as the accepted document leaves them** (E2E round-3 finding V4-builder).
   * The type bar reads the *staged* type list, so the script's "turn some of my harvest bees into
   * builders" produced a builder the audience could not see named anywhere. This is the only
   * component that learns what the agent wrote, so it is the one that says so.
   */
  onTypesApplied?: (types: readonly DemoStageTypeSpec[]) => void;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  storage?: StorageLike;
  random?: PracticeRandom;
  /** Defaults to the real `openPracticeSession`. */
  openSession?: (config: OpenPracticeSessionConfig) => Promise<PracticeOpenOutcome>;
  /** Defaults to `(config) => new RoundsHttpClient(config)`. */
  createClient?: (config: RoundsHttpConfig) => DemoPracticeClient;
}

function windowLocalStorage(): StorageLike {
  return {
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
    removeItem: (key) => window.localStorage.removeItem(key),
  };
}

/** The document's own grammar (BPP-SPEC §15): one `birth_type` line, then one `type <name> buffs
 * <buff>` header per block (DESIGN §5's syntax sketch — no colon on that line), each followed by
 * its program body verbatim.
 *
 * **Every non-empty `program` staged by `chapters.ts` already carries its own `type <name> buffs
 * <buff>` header** (`stage-driver.ts`'s `DemoStageTypeSpec`, task 13.3 — the C-1 compiler requires
 * one). Composing a second header on top doubles it, and the server's document parser
 * (`waggle-bpp/src/document.rs` `parse_blocks`) starts a new block at *every* `type` line — so the
 * doubled header parses as an empty-bodied block plus a duplicate type name, and the real server
 * refuses the whole-document seed write (playthrough finding, 2026-08-09). Only compose the header
 * for the header-less case (`stage-driver.ts`'s deliberate empty-string "no program yet" program). */
function composeDemoDocument(birthType: string, types: readonly DemoStageTypeSpec[]): string {
  const blocks = types.map((type) => {
    const body = type.program.startsWith('type ') ? type.program : `type ${type.name} buffs ${type.buff}\n${type.program}`;
    return body.replace(/\n+$/, '');
  });
  return [`birth_type ${birthType}`, ...blocks].join('\n\n') + '\n';
}

function failureMessageFor(reasonKind: string): string {
  switch (reasonKind) {
    case 'stale':
      return 'The document moved on — reopening and trying again.';
    case 'unauthorized':
      return "The Beekeeper didn't recognise that code.";
    default:
      return 'Something went wrong reaching the Beekeeper.';
  }
}

export function BeekeeperPromptBar({
  driver,
  colony = 0,
  birthType,
  seedTypes,
  collapseChat = false,
  onTypesApplied,
  baseUrl,
  fetchImpl,
  storage,
  random,
  openSession = openPracticeSession,
  createClient = (config) => new RoundsHttpClient(config),
}: BeekeeperPromptBarProps): ReactElement {
  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState<PromptBarStatus>('idle');
  const [failureMessage, setFailureMessage] = useState<string | undefined>(undefined);
  const [seam, setSeam] = useState<ChatSeam | null>(null);
  const pendingTextRef = useRef<string>('');
  const appliedTypesRef = useRef<Map<string, string>>(new Map());
  const chatSnapshot = useChatSeam(seam ?? undefined);

  // The overlay's own `close`, driven by the cue rather than by the ✕ — nothing else about the
  // seam changes, so a later message reopens it exactly as it always did.
  //
  // **Stop first when a turn is still running** (E2E round-1 finding M-1, 2026-08-09).
  // `chat-channel.ts`'s `close()` deliberately no-ops while `running` is true — `ChatOverlay`'s own
  // modality only offers Stop, never Close, until a turn has actually settled — so calling it
  // unconditionally here silently failed whenever the show advanced past this beat before a
  // still-running (or looping) canned turn had finished: the overlay stayed mounted and kept
  // receiving and rendering turn events — accreting duplicate-keyed rows on a repeating canned
  // reply — for the rest of the show, long after the presenter believed it was gone. `Stop`, then
  // let `turnFinished` land and close it, mirrors `ChatOverlay`'s own `pendingAutoCloseRef` pattern
  // one level up rather than inventing a second one.
  //
  // **Rekeyed to the channel's `phase`, not bare `transcript.running`** (`beekeeper-progress-
  // indicator` §2 — this file is the one place outside `ChatOverlay` itself the plan calls out by
  // name as needing to change). The original guard above only knew about M-1's own case: a
  // collapse that lands while `running` stops first. But `chat-channel.ts`'s `close()` ALSO
  // permits closing while `submitting` or `uncertain` (there is no turn id to stop yet) — so a
  // collapse that landed in that window used to fall straight to `seam.close()`, below, hiding the
  // overlay while the send was still in flight. The turn could then start and run invisibly: the
  // exact M-1 failure mode, reopened one phase earlier. So `submitting`/`uncertain` now latch and
  // WAIT rather than closing immediately — the overlay stays visible (Close is already offered and
  // honoured for real by the player in that state; this component simply declines to invoke it on
  // their behalf until there is evidence either way).
  const pendingCollapseRef = useRef(false);
  useEffect(() => {
    if (!collapseChat || seam === null) return;
    const phase = chatSnapshot?.phase;
    if (phase === 'running') {
      pendingCollapseRef.current = true;
      seam.stop();
      return;
    }
    if (phase === 'submitting' || phase === 'uncertain') {
      // Admission is not yet known. Latch and let the phase-watching effect below decide once it
      // moves — closing now would risk exactly what M-1 was about: a turn starting unseen.
      pendingCollapseRef.current = true;
      return;
    }
    // `idle` or `failed` (or no seam ready yet) — nothing in flight, safe to close outright.
    seam.close();
    // `chatSnapshot` is read as of the moment `collapseChat` (or `seam`) actually changes, not
    // reactively — the effect below is what reacts to the phase moving on its own afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseChat, seam]);

  // Re-evaluated every time the phase itself moves while latched: a `submitting` send can become a
  // `running` turn (now stoppable, for the first time) or resolve straight to `idle`/`failed` (now
  // closeable) with no further cue from the show — this is what lets the latch above ever resolve.
  useEffect(() => {
    if (!pendingCollapseRef.current || seam === null) return;
    const phase = chatSnapshot?.phase;
    if (phase === 'running') {
      seam.stop();
      return;
    }
    if (phase === 'submitting' || phase === 'uncertain') {
      return; // still waiting for evidence
    }
    pendingCollapseRef.current = false;
    seam.close();
  }, [chatSnapshot?.phase, seam]);

  /**
   * Pushes an accepted document's type set into the **local** world (DD8).
   *
   * **Creations land before swaps, and the swaps wait for them.** A document that adds a `builder`
   * and rewrites `harvester` to reference it is the chapter's own scripted prompt, and the colony
   * read lists types in registration order — so `harvester` is reached first. Issued in list order,
   * its hot-swap compiles against a registry that has no `builder` yet and is refused
   * `type "builder" is absent`, while the create that would have fixed it is still in flight across
   * the worker boundary. Both refusals were swallowed, and chapter 3's payoff simply never
   * appeared. So this is one ordered pass with an await between the halves, not a loop.
   *
   * A refusal is still swallowed per type: the chat overlay is the presenter's feedback surface for
   * this beat, and the stage carries on. What is *not* swallowed is the ordering.
   */
  async function applyColonyMeta(meta: ColonyMeta): Promise<void> {
    const added = meta.types.filter((type) => !appliedTypesRef.current.has(type.name));
    const changed = meta.types.filter((type) => {
      const previous = appliedTypesRef.current.get(type.name);
      return previous !== undefined && previous !== type.canonicalText;
    });
    for (const type of meta.types) {
      appliedTypesRef.current.set(type.name, type.canonicalText);
    }
    await Promise.all(
      added.map((type) =>
        // `buffName` arrives from the colony read in Rust `Debug` spelling (`Building`); C-1 wants
        // the B++ terminal (`building`). `c1BuffName` is where the two meet — see its own note.
        Promise.resolve(
          driver.createType?.({
            colony,
            name: type.name,
            buff: c1BuffName(type.buffName),
            source: type.canonicalText,
          }),
        ).catch(() => undefined),
      ),
    );
    await Promise.all(
      changed.map((type) =>
        driver.hotSwap({ colony, type: type.name, source: type.canonicalText }).catch(() => undefined),
      ),
    );
    // Announced after the world has them, not before: a chip for a type the local run refused
    // would be a bar that disagrees with the field the audience is looking at.
    onTypesApplied?.(
      meta.types.map((type) => ({
        name: type.name,
        buff: c1BuffName(type.buffName),
        program: type.canonicalText,
      })),
    );
  }

  /** Mints/reuses the practice sandbox and seeds it with `seedTypes` — the one-time setup a first
   * message needs. Returns the ready {@link ChatSeam}, or `null` on a refusal (already surfaced
   * as the bar's failure state). */
  async function ensureSeam(): Promise<ChatSeam | null> {
    const credentialStore = createPracticeCredentialStore(storage ?? windowLocalStorage(), random ?? cryptoPracticeRandom());
    const record = credentialStore.ensure();
    const opened = await openSession({ baseUrl, fetchImpl, credential: record.credential });
    if (opened.kind !== 'created' && opened.kind !== 'resumed') {
      setFailureMessage('Could not reach the practice hive.');
      return null;
    }
    const client = createClient({
      baseUrl: baseUrl ?? '',
      scope: opened.session.scope,
      token: opened.session.token,
      fetchImpl,
    });
    const seedDocument = composeDemoDocument(birthType, seedTypes);
    const seeded = await client.handEditDocument(seedDocument, null);
    if (seeded.kind === 'refused') {
      setFailureMessage(failureMessageFor(seeded.reason.kind));
      return null;
    }
    appliedTypesRef.current = new Map(seedTypes.map((type) => [type.name, type.program]));
    const chatClient = client.createChatClient();
    const channel = createChatChannel({
      transport: {
        sendMessage: (text, targetType, handlers) => chatClient.sendMessage(text, targetType, handlers),
        attachTurn: (turnId, afterCursor, handlers) => chatClient.attachTurn(turnId, afterCursor, handlers),
        stopTurn: (turnId) => chatClient.stopTurn(turnId),
        fetchThread: () => chatClient.fetchThread(),
        // Omitted rather than stubbed when the client cannot clear (a test double): an absent
        // method is what keeps "New chat" off a surface that could not honour it.
        ...(chatClient.clearThread === undefined
          ? {}
          : { clearThread: () => chatClient.clearThread!() }),
        fetchColony: () => client.fetchColony(),
      },
      store: { applyColonyMeta },
    });
    setSeam(channel);
    return channel;
  }

  async function submit(text: string): Promise<void> {
    setStatus('busy');
    setFailureMessage(undefined);
    pendingTextRef.current = text;
    const channel = seam ?? (await ensureSeam());
    if (channel === null) {
      setStatus('failed');
      return;
    }
    channel.sendAndOpen(text, null);
    setStatus('idle');
    setDraft('');
  }

  return (
    <>
      <PromptBar
        scopeLabel="Beekeeper"
        draft={draft}
        status={status}
        failureMessage={failureMessage}
        onChangeDraft={setDraft}
        onSubmit={() => void submit(draft)}
        onRetry={() => void submit(pendingTextRef.current)}
      />
      {chatSnapshot?.view === 'open' && (
        <ChatOverlay
          transcript={chatSnapshot.transcript.items}
          phase={chatSnapshot.phase}
          awaitingFirstOutput={chatSnapshot.awaitingFirstOutput}
          noOutputSettle={chatSnapshot.noOutputSettle}
          onSend={(text) => seam?.sendAndOpen(text, null)}
          onStop={() => seam?.stop()}
          // Minimise, exactly as the two game surfaces wire it (`player-chat-and-outcome` **D-2**):
          // the presenter's ✕ puts the panel away and the turn carries on, which is what the show
          // needs — the fast-forward and the hive reveal are the screens the audience is there for.
          //
          // **This mount alone has no `ChatReentryRow`.** The presentation's `PromptBar` is a
          // different control with no re-entry row above it, so the way back from a minimised
          // conversation here is sending again (`sendAndOpen` republishes `view: 'open'`), not a
          // tap on a report line. That is the same way back this chapter has always had — before
          // **D-1** the ✕ was not even offered mid-turn — so nothing is lost; it is simply not the
          // full **D-3** treatment, and it is stated here rather than left to be discovered.
          onClose={() => seam?.minimise()}
          onRefresh={() => void seam?.rehydrate()}
          {...(seam?.clearConversation === undefined
            ? {}
            : { onClear: () => void seam.clearConversation!() })}
        />
      )}
    </>
  );
}
